import React from 'react';
import {
    Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Drawer, FormControl,
    IconButton, InputLabel, MenuItem, Select, TextField, Tooltip, Typography
} from '@material-ui/core/';
import { TreeView, TreeItem } from '@material-ui/lab';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import ShareIcon from '@material-ui/icons/Share';
import LockIcon from '@material-ui/icons/Lock';
import DescriptionIcon from '@material-ui/icons/Description';
import AddIcon from '@material-ui/icons/Add';
import NoteAddIcon from '@material-ui/icons/NoteAdd';
import CreateNewFolderIcon from '@material-ui/icons/CreateNewFolder';
import FolderIcon from '@material-ui/icons/Folder';
// A chip rather than a play button: an algorithm is a thing that computes, not a thing this row runs.
import AlgorithmIcon from '@material-ui/icons/Memory';

import EnhancedTable from '@apricityhealth/web-common-lib/components/EnhancedTable';
import SelectUser from '@apricityhealth/web-common-lib/components/SelectUser';
import User from '@apricityhealth/web-common-lib/components/User';
import Config from '@apricityhealth/web-common-lib/Config';
import Axios from '@apricityhealth/web-common-lib/utils/Axios';
import getErrorMessage from '@apricityhealth/web-common-lib/utils/getErrorMessage';

import MLAlgorithmView, { isManifestFile } from './MLAlgorithmView';

const ACCESS_LEVELS = ['read', 'write', 'admin'];

/** What a brand-new algorithm starts as. A manifest, because without one nothing lists or runs it. */
const STARTER_MANIFEST = (name) => JSON.stringify({
    name,
    description: '',
    script: 'main.py',
    dependencies: [],
    opts: []
}, null, 4);

const STARTER_SCRIPT = `import sys
import json

# Options arrive as a single JSON argument; 'biomarkers' is the path to the cohort CSV.
opts = json.loads(sys.argv[1]) if len(sys.argv) > 1 else {}

from common import loadPatientData

data = loadPatientData(opts)

# Anything printed on stdout as JSON becomes the job result.
print(json.dumps({ "rows": 0 if data is None else len(data) }))
`;

/**
 * The algorithm library — docs/plans/ml-models-agent.md §4.
 *
 * **A tree, not a list, because the data is one.** Three namespaces, each holding algorithms, each holding
 * files — and the namespace is the most important thing on the screen, since it decides what you may do:
 *
 * - `system/` — the built-ins. Read-only unless you are an administrator, so the way to change one is Copy.
 * - `users/<you>/` — yours, private.
 * - `shared/<name>/` — shared with named people at read, write or admin, held in the folder's `.owner.json`.
 *
 * A flat list had to spend two columns (`root`, `ref`) restating the hierarchy, and buried the files one more
 * click down inside the editor. Here the shape carries it.
 *
 * **Nothing opens on its own.** Expanding an algorithm lists its files and nothing more; the editor opens only
 * from the Edit action, or by clicking the file you actually want — which is a choice of a leaf, not a
 * side-effect of selecting a row.
 *
 * Sharing lives on the shared FOLDER node rather than on an algorithm, because that is what `.owner.json`
 * governs. An algorithm of your own is shared by copying it into such a folder.
 */
export class MLAlgorithmsView extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            algorithms: [],
            folders: [],
            // Who the server says we are. Not decoded from the token here — the server already answers this,
            // and a second answer derived in the client is a second answer that can disagree.
            userId: '',
            isAdmin: false,
            filesByRef: {},         // lazily filled as algorithms are expanded
            // Bumped on every reload. A file request still in flight when the list reloads belongs to the
            // previous view of the world — one reported "No readable manifest.json" for an algorithm that had
            // just been deleted, landing after the reload had already cleared the error.
            generation: 0,
            // False only until the FIRST load settles. Every "nothing here" message is a claim about the
            // library, and before the first response there is nothing to base one on — an empty tree said
            // "signing in…", "none", "no shared folders" for a moment on every visit, all three of them
            // guesses. Later reloads keep the tree on screen and use the toolbar spinner instead.
            loaded: false,
            // The three namespaces open, their algorithms visible, and nothing below that — one level, so the
            // shape of the library is the first thing on screen without a wall of files.
            expanded: ['ns:users', 'ns:shared', 'ns:system'],
            // TWO slots, and the separation is the point. The editor lived in `dialog` alongside every modal,
            // so opening any dialog UNMOUNTED it — including the "discard unsaved changes?" confirm, which
            // destroyed the very changes it was asking about and left Cancel with nothing to go back to. A
            // modal must be able to appear over the editor without replacing it.
            drawer: null,
            dialog: null,
            // The open editor reports this up, because only it knows whether a switch would lose typing —
            // and only this component knows a switch is about to happen.
            dirty: false,
            progress: null,
            error: null
        };
    }

    componentDidMount() {
        this.load();
    }

    request(path, method, { params, data } = {}) {
        const { appContext: { state: { idToken } } } = this.props;
        return Axios({
            url: Config.baseUrl + `${Config.pathPrefix}apricity-forecast/ml/${path}`,
            method,
            headers: { Authorization: idToken },
            ...(params ? { params } : {}),
            ...(data ? { data } : {})
        });
    }

    load() {
        this.setState({
            generation: this.state.generation + 1,
            progress: <CircularProgress size={20} />,
            error: null
        });
        Promise.all([
            this.request('algorithms', 'GET'),
            this.request('shared', 'GET')
        ]).then(([algorithms, shared]) => {
            this.setState({
                algorithms: (algorithms.data && algorithms.data.algorithms) || [],
                folders: (shared.data && shared.data.folders) || [],
                userId: (shared.data && shared.data.userId) || '',
                isAdmin: !!(shared.data && shared.data.isAdmin),
                // Dropped on reload rather than kept: a file list cached across a delete or a copy is a list
                // of files that may no longer be there.
                filesByRef: {},
                loaded: true,
                progress: null
            }, this.loadFiles.bind(this));      // re-hydrate whatever is still expanded
        }).catch((error) => {
            // `loaded` is set on failure too: a spinner that never stops is a worse answer than an error.
            this.setState({ loaded: true, progress: null, error: getErrorMessage(error) });
        });
    }

    /**
     * Close the drawer. **No reload** — the editor calls `onSaved` when it actually changes something.
     *
     * Reloading on close meant merely looking at a file rebuilt the whole tree, which is where the emptying
     * came from. Nothing to refresh if nothing changed.
     */
    onCloseDialog() {
        this.setState({ drawer: null, dirty: false });
    }

    // -------------------------------------------------------------------------------------------------
    // Tree
    // -------------------------------------------------------------------------------------------------

    /**
     * Fetch an algorithm's files the first time its node is expanded.
     *
     * The list response deliberately omits them — including files would cost one S3 listing per algorithm on
     * every page load, for a level most visits never open.
     */
    onToggle(event, expanded) {
        this.setState({ expanded }, () => this.loadFiles(expanded));
    }

    /**
     * Fetch the file list for every expanded algorithm that does not have one.
     *
     * **Driven by state, not by the toggle event.** Fetching only on toggle meant a reload — which drops
     * `filesByRef` because a cached list can outlive a delete — left every expanded node showing "loading…"
     * with nothing on its way, and the only cure was collapsing and re-expanding it. Closing a file did exactly
     * that: the tree appeared to empty and then hang.
     */
    loadFiles(expanded = this.state.expanded) {
        const { algorithms, generation } = this.state;

        expanded.filter((id) => id.startsWith('algo:')).forEach((id) => {
            const ref = id.slice('algo:'.length);
            if (this.state.filesByRef[ref] !== undefined) return;   // already loaded, or in flight
            this.setState({ filesByRef: { ...this.state.filesByRef, [ref]: null } });
            const algorithm = algorithms.find((a) => a.ref === ref);
            this.request('algorithm', 'GET', { params: { ref } }).then(({ data }) => {
                if (this.state.generation !== generation) return;    // a reload happened; this answer is stale
                // The manifest is not shown as a file — it is the algorithm's properties, edited by the form.
                this.setState({
                    filesByRef: {
                        ...this.state.filesByRef,
                        [ref]: (data.files || []).filter((f) => !isManifestFile(f))
                    }
                });
            }).catch((error) => {
                if (this.state.generation !== generation) return;
                this.setState({
                    filesByRef: { ...this.state.filesByRef, [ref]: [] },
                    error: `${algorithm ? algorithm.name : ref}: ${getErrorMessage(error)}`
                });
            });
        });
    }

    /**
     * Open the editor on an algorithm, optionally at a particular file.
     *
     * Asks first when the editor already open has unsaved typing — clicking another file in the tree is one
     * click away from silently discarding it, and the drawer gives no hint that it happened.
     */
    onEdit(algorithm, file) {
        if (this.state.dirty) {
            // Goes in the DIALOG slot while the editor stays in the drawer slot, so Cancel is a real cancel:
            // the component was never unmounted and its unsaved text is still in its own state.
            return this.setState({
                dialog: <Dialog open={true}>
                    <DialogTitle>Discard unsaved changes?</DialogTitle>
                    <DialogContent>
                        <Typography>
                            The editor has changes that have not been saved. Opening
                            {' '}<code>{file || (algorithm.name || algorithm.ref)}</code> loses them.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => this.setState({ dialog: null })}>Cancel</Button>
                        <Button color="secondary" onClick={() => this.setState({ dialog: null, dirty: false },
                            () => this.openEditor(algorithm, file))}>
                            Discard
                        </Button>
                    </DialogActions>
                </Dialog>
            });
        }
        this.openEditor(algorithm, file);
    }

    openEditor(algorithm, file) {
        this.setState({
            dirty: false,
            drawer: <Drawer variant="persistent" anchor="right" open={true}>
                <MLAlgorithmView appContext={this.props.appContext} algorithm={algorithm} openFile={file}
                    onClose={this.onCloseDialog.bind(this)}
                    onDirtyChange={(dirty) => this.setState({ dirty })}
                    onSaved={this.load.bind(this)} />
            </Drawer>
        });
    }

    /**
     * The destinations this user can write to.
     *
     * `system/` is offered only to an administrator, because the store will refuse it for anyone else — and an
     * option that always errors is worse than no option.
     */
    destinations() {
        const { folders, userId, isAdmin } = this.state;
        // Same word as the tree section, so "Where: Private" and the branch it lands in read as the same place.
        const list = userId ? [{ value: `users/${userId}`, label: 'Private' }] : [];
        (folders || []).forEach((f) => {
            const mine = (f.owners || []).find((o) => o.userId === userId);
            // A read-only share is not somewhere you can put an algorithm, and an administrator reaches every
            // folder regardless of what the ACL says about them.
            if (isAdmin || (mine && mine.access !== 'read')) {
                list.push({ value: `shared/${f.name}`, label: `Shared: ${f.name}` });
            }
        });
        if (isAdmin) list.push({ value: 'system', label: 'System (built-in)' });
        return list;
    }

    /**
     * Create an algorithm in a specific place.
     *
     * `destination` comes from the node the action was pressed on — `users/<me>`, `system`, or
     * `shared/<name>` — so there is nothing to choose. The old toolbar button had to ask "Where", which is a
     * question the tree already answers.
     */
    onNew(destination) {
        this.setState({
            dialog: <NameDialog
                title={`New algorithm in ${this.labelFor(destination)}`}
                label="Algorithm id"
                destination={destination}
                onCancel={() => this.setState({ dialog: null })}
                onDone={({ id }) => {
                    const ref = `${destination}/${id}`;
                    this.setState({ dialog: null, progress: <CircularProgress size={20} />, error: null });
                    // The manifest goes first: it is what makes the folder an algorithm rather than a few loose
                    // files, and if the second write fails the half-made thing is at least visible and editable.
                    this.request('algorithm/file', 'POST', {
                        data: { ref, file: 'manifest.json', body: STARTER_MANIFEST(id), note: 'created' }
                    }).then(() => this.request('algorithm/file', 'POST', {
                        data: { ref, file: 'main.py', body: STARTER_SCRIPT, note: 'created' }
                    })).then(() => {
                        this.setState({ progress: null }, this.load.bind(this));
                    }).catch((error) => {
                        this.setState({ progress: null, error: getErrorMessage(error) });
                    });
                }} />
        });
    }

    /** Can this caller put an algorithm into that shared folder? */
    mayWriteShared(folder) {
        if (this.state.isAdmin) return true;
        const mine = (folder.owners || []).find((o) => o.userId === this.state.userId);
        return !!mine && mine.access !== 'read';
    }

    /**
     * Can this caller remove the folder itself?
     *
     * Admin, not write — deleting the folder removes the `.owner.json` that grants everyone else their
     * access, which is the same authority sharing it required. The server enforces this; repeating it here
     * only decides whether the button is offered.
     */
    mayAdminShared(folder) {
        if (this.state.isAdmin) return true;
        const mine = (folder.owners || []).find((o) => o.userId === this.state.userId);
        return !!mine && mine.access === 'admin';
    }

    /** A destination as it reads in a dialog title. */
    labelFor(destination) {
        if (destination === 'system') return 'System';
        if (destination.startsWith('shared/')) return destination.slice('shared/'.length);
        return 'Private';
    }

    /**
     * Add a file to an algorithm.
     *
     * A name containing a slash creates a subdirectory, because in S3 that is all a directory is — there is no
     * empty folder to create, so "add folder" would be a button that does nothing until a file goes in it.
     */
    /**
     * A folder needs a first file to exist at all.
     *
     * S3 has no directories — a prefix exists because something is under it — so "new folder" asks for both
     * rather than pretending an empty one can be created and leaving nothing behind.
     */
    onNewFolder(destination) {
        this.setState({
            dialog: <NameDialog
                title={`New folder in ${this.labelFor(destination)}`}
                label="Folder and first file"
                defaultId="helpers/__init__.py"
                helperText="A folder only exists once it holds a file, so name both — e.g. helpers/utils.py"
                onCancel={() => this.setState({ dialog: null })}
                onDone={({ id }) => {
                    const slash = id.indexOf('/');
                    if (slash <= 0) {
                        return this.setState({ dialog: null,
                            error: `"${id}" needs a folder and a file, e.g. helpers/utils.py` });
                    }
                    const ref = `${destination}/${id.slice(0, slash)}`;
                    const file = id.slice(slash + 1);
                    this.setState({ dialog: null, progress: <CircularProgress size={20} />, error: null });
                    this.request('algorithm/file', 'POST', {
                        data: { ref, file, body: file.endsWith('.py') ? `# ${file}\n` : '', note: 'created' }
                    }).then(() => {
                        this.setState({ progress: null }, this.load.bind(this));
                    }).catch((error) => {
                        this.setState({ progress: null, error: getErrorMessage(error) });
                    });
                }} />
        });
    }

    onAddFile(algorithm) {
        this.setState({
            dialog: <NameDialog
                title={`New file in ${algorithm.name || algorithm.algorithmId}`}
                label="File name"
                defaultId="helper.py"
                helperText="A name like sub/util.py puts it in a subdirectory."
                onCancel={() => this.setState({ dialog: null })}
                onDone={({ id }) => {
                    this.setState({ dialog: null, progress: <CircularProgress size={20} />, error: null });
                    const body = id.endsWith('.py')
                        ? `# ${id}\n`
                        : (id.endsWith('.json') ? '{}\n' : '');
                    this.request('algorithm/file', 'POST', {
                        data: { ref: algorithm.ref, file: id, body, note: 'created' }
                    }).then(() => {
                        // Drop this algorithm's cached list so loadFiles fetches it again, then open the new
                        // file — creating a file and not being taken to it is a click for nothing.
                        this.setState({
                            progress: null,
                            filesByRef: { ...this.state.filesByRef, [algorithm.ref]: undefined }
                        }, () => { this.loadFiles(); this.onEdit(algorithm, id); });
                    }).catch((error) => {
                        this.setState({ progress: null, error: getErrorMessage(error) });
                    });
                }} />
        });
    }

    onDeleteFile(algorithm, file) {
        this.setState({
            dialog: <Dialog open={true}>
                <DialogTitle>Delete {file}?</DialogTitle>
                <DialogContent>
                    <Typography>
                        Removed from <code>{algorithm.ref}</code>. Previous versions stay in the file's history,
                        but nothing in this screen brings the file itself back.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => this.setState({ dialog: null })}>Cancel</Button>
                    <Button color="secondary" onClick={() => {
                        this.setState({ dialog: null, progress: <CircularProgress size={20} />, error: null });
                        this.request('algorithm', 'DELETE', { params: { ref: algorithm.ref, file } }).then(() => {
                            this.setState({
                                progress: null,
                                filesByRef: { ...this.state.filesByRef, [algorithm.ref]: undefined }
                            }, this.loadFiles.bind(this));
                        }).catch((error) => {
                            this.setState({ progress: null, error: getErrorMessage(error) });
                        });
                    }}>Delete</Button>
                </DialogActions>
            </Dialog>
        });
    }

    onCopy(algorithm) {
        this.setState({
            dialog: <NameDialog
                title={`Copy ${algorithm.name || algorithm.ref}`}
                label="New algorithm id"
                defaultId={algorithm.algorithmId}
                destinations={this.destinations()}
                onCancel={() => this.setState({ dialog: null })}
                onDone={({ id, destination }) => {
                    this.setState({ dialog: null, progress: <CircularProgress size={20} />, error: null });
                    this.request('algorithm/copy', 'POST', {
                        data: { from: algorithm.ref, to: `${destination}/${id}`,
                            note: `copied from ${algorithm.ref}` }
                    }).then(() => {
                        this.setState({ progress: null }, this.load.bind(this));
                    }).catch((error) => {
                        this.setState({ progress: null, error: getErrorMessage(error) });
                    });
                }} />
        });
    }

    /**
     * Remove a shared folder.
     *
     * No force option here on purpose. The server refuses while algorithms remain, and the useful answer to
     * that is to go and look at them — a checkbox that deletes other people's work in one click is not a
     * kindness, and this dialog cannot show whose work it is.
     */
    onDeleteSharedFolder(folder) {
        const count = this.state.algorithms
            .filter((a) => a.root === 'shared' && a.ref.split('/')[1] === folder.name).length;
        this.setState({
            dialog: <Dialog open={true}>
                <DialogTitle>Delete shared folder {folder.name}?</DialogTitle>
                <DialogContent>
                    {count > 0
                        ? <Typography>
                            <code>{folder.name}</code> still holds {count} algorithm{count === 1 ? '' : 's'}.
                            Delete or move {count === 1 ? 'it' : 'them'} first — a shared folder can hold work
                            belonging to any of its {(folder.owners || []).length} member(s).
                        </Typography>
                        : <Typography>
                            The folder and its sharing are removed, and its {(folder.owners || []).length}
                            {' '}member(s) lose access. Past versions stay recoverable in S3.
                        </Typography>}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => this.setState({ dialog: null })}>Cancel</Button>
                    <Button color="secondary" disabled={count > 0} onClick={() => {
                        this.setState({ dialog: null, progress: <CircularProgress size={20} />, error: null });
                        this.request(`shared/${encodeURIComponent(folder.name)}`, 'DELETE').then(() => {
                            this.setState({ progress: null }, this.load.bind(this));
                        }).catch((error) => {
                            this.setState({ progress: null, error: getErrorMessage(error) });
                        });
                    }}>Delete</Button>
                </DialogActions>
            </Dialog>
        });
    }

    onDelete(algorithm) {
        this.setState({
            dialog: <Dialog open={true}>
                <DialogTitle>Delete {algorithm.name || algorithm.ref}?</DialogTitle>
                <DialogContent>
                    <Typography>
                        Every file in <code>{algorithm.ref}</code> is removed. Models that reference it will
                        fail on their next run.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => this.setState({ dialog: null })}>Cancel</Button>
                    <Button color="secondary" onClick={() => {
                        this.setState({ dialog: null, progress: <CircularProgress size={20} />, error: null });
                        this.request('algorithm', 'DELETE', { params: { ref: algorithm.ref } }).then(() => {
                            this.setState({ progress: null }, this.load.bind(this));
                        }).catch((error) => {
                            this.setState({ progress: null, error: getErrorMessage(error) });
                        });
                    }}>Delete</Button>
                </DialogActions>
            </Dialog>
        });
    }

    onShare(folderName) {
        const { folders } = this.state;
        this.setState({
            dialog: <ShareFolderDialog
                appContext={this.props.appContext}
                folders={folders}
                initialName={folderName}
                userId={this.state.userId}
                onCancel={() => this.setState({ dialog: null })}
                onSave={({ name, owners }) => {
                    this.setState({ dialog: null, progress: <CircularProgress size={20} />, error: null });
                    this.request(`shared/${encodeURIComponent(name)}`, 'POST', { data: { owners } }).then(() => {
                        this.setState({ progress: null }, this.load.bind(this));
                    }).catch((error) => {
                        this.setState({ progress: null, error: getErrorMessage(error) });
                    });
                }} />
        });
    }

    // -------------------------------------------------------------------------------------------------
    // Rendering
    // -------------------------------------------------------------------------------------------------

    /**
     * One row's label: name, description, and the actions for it.
     *
     * `stopPropagation` on every action is what keeps a click on Delete from also toggling the node — the
     * button sits inside the TreeItem's label, which is the element the tree listens on.
     */
    /**
     * An action button that lives inside a TreeItem label.
     *
     * `stopPropagation` is what keeps a click on Delete from also toggling the node — the button sits inside
     * the element the tree listens on. The wrapping span is for the tooltip: a disabled MUI button fires no
     * pointer events, so a Tooltip around it directly never explains why it is disabled.
     */
    action(title, icon, onClick, disabled = false) {
        return <Tooltip key={title} title={title}>
            <span>
                <IconButton size="small" disabled={disabled}
                    onClick={(e) => { e.stopPropagation(); onClick(); }}>{icon}</IconButton>
            </span>
        </Tooltip>;
    }

    /** A root or folder row: its name, and the things that can be created inside it. */
    branchLabel(text, actions, note) {
        return <div style={{ display: 'flex', alignItems: 'center', padding: '2px 0' }}>
            <span style={{ minWidth: 220 }}><b>{text}</b></span>
            <span style={{ flexGrow: 1, opacity: 0.7, fontSize: 13 }}>{note}</span>
            {actions}
        </div>;
    }

    /**
     * A folder that is not an algorithm — `system/common` is the shared Python library every algorithm
     * imports, and it has no manifest because there is nothing in it to run.
     *
     * So it gets no Edit-properties (there are no properties) and no Copy (a copy of it would not be an
     * algorithm either). Its files are edited like any others.
     */
    folderLabel(folder) {
        const mayWrite = folder.access === 'write';
        const action = this.action.bind(this);

        return <div style={{ display: 'flex', alignItems: 'center', padding: '2px 0' }}>
            <FolderIcon fontSize="small" style={{ opacity: 0.6, marginRight: 6 }} />
            <span style={{ minWidth: 214 }}>{folder.algorithmId}</span>
            {/* No caption. The folder icon and the absence of any run action already say what this is, and the
                same sentence on every folder is noise. Not read from the server either, so it stays gone
                regardless of which build is deployed. */}
            <span style={{ flexGrow: 1 }} />
            {action(mayWrite ? 'Add a file' : 'Read only', <NoteAddIcon fontSize="small" />,
                () => this.onAddFile(folder), !mayWrite)}
            {action(mayWrite ? 'Delete this folder and its files' : 'Read only',
                <DeleteIcon fontSize="small" />, () => this.onDelete(folder), !mayWrite)}
        </div>;
    }

    algorithmLabel(algorithm) {
        if (algorithm.isFolder) return this.folderLabel(algorithm);
        const mayWrite = algorithm.access === 'write';
        const action = this.action.bind(this);

        return <div style={{ display: 'flex', alignItems: 'center', padding: '2px 0' }}>
            <Tooltip title="Runnable algorithm">
                <AlgorithmIcon fontSize="small" style={{ opacity: 0.6, marginRight: 6 }} />
            </Tooltip>
            {!mayWrite && <Tooltip title="Read only">
                <LockIcon fontSize="small" style={{ opacity: 0.5, marginRight: 4 }} />
            </Tooltip>}
            <span style={{ minWidth: mayWrite ? 214 : 194 }}>{algorithm.name || algorithm.algorithmId}</span>
            <span style={{ flexGrow: 1, opacity: 0.7, fontSize: 13, paddingRight: 10 }}>
                {algorithm.description}
            </span>
            {action(mayWrite ? 'Edit properties' : 'View properties (read only)',
                <EditIcon fontSize="small" />, () => this.onEdit(algorithm))}
            {action(mayWrite ? 'Add a file' : 'Read only — copy it to add files',
                <NoteAddIcon fontSize="small" />, () => this.onAddFile(algorithm), !mayWrite)}
            {action('Copy to Private or a shared folder', <FileCopyIcon fontSize="small" />,
                () => this.onCopy(algorithm))}
            {action(mayWrite ? 'Delete this algorithm' : 'Read only — copy it to change it',
                <DeleteIcon fontSize="small" />, () => this.onDelete(algorithm), !mayWrite)}
        </div>;
    }

    /** An algorithm node, with its files underneath once it has been opened. */
    algorithmNode(algorithm) {
        const files = this.state.filesByRef[algorithm.ref];
        return <TreeItem key={algorithm.ref} nodeId={`algo:${algorithm.ref}`}
            label={this.algorithmLabel(algorithm)}>
            {files === undefined || files === null
                ? <TreeItem nodeId={`loading:${algorithm.ref}`} label={<span style={{ opacity: 0.6 }}>
                    loading…</span>} />
                : (files.length
                    ? files.map((f) => <TreeItem key={f} nodeId={`file:${algorithm.ref}/${f}`}
                        icon={<DescriptionIcon fontSize="small" style={{ opacity: 0.6 }} />}
                        onClick={() => this.onEdit(algorithm, f)}
                        label={<div style={{ display: 'flex', alignItems: 'center' }}>
                            <span style={{ flexGrow: 1, fontFamily: 'Consolas, Menlo, monospace', fontSize: 13 }}>
                                {f}
                            </span>
                            {algorithm.access === 'write' && this.action('Delete this file',
                                <DeleteIcon fontSize="small" />, () => this.onDeleteFile(algorithm, f))}
                        </div>} />)
                    : <TreeItem nodeId={`empty:${algorithm.ref}`}
                        label={<span style={{ opacity: 0.6 }}>no files</span>} />)}
        </TreeItem>;
    }

    render() {
        const { algorithms, folders, userId, isAdmin, expanded, loaded, drawer, dialog, progress,
            error } = this.state;

        const priv = algorithms.filter((a) => a.root === 'users');
        const system = algorithms.filter((a) => a.root === 'system');
        const sharedFolderOf = (a) => (a.ref.split('/')[1] || '');
        const sharedAlgorithms = algorithms.filter((a) => a.root === 'shared');

        // One wording for every empty branch. The old per-branch hints ("signing in…", "use Copy on one") read
        // as status rather than content, and three different phrasings for the same fact is three things to
        // read before noticing they all mean nothing is there.
        let emptyKey = 0;
        const empty = () => <TreeItem nodeId={`empty:${++emptyKey}`}
            label={<span style={{ opacity: 0.6 }}>&lt;EMPTY&gt;</span>} />;

        return <div style={{ margin: 10 }}>
            {/*
                No create buttons up here any more. Every "new" action now hangs off the node it creates into,
                which is both where you are looking when you want it and the answer to the "Where" question the
                old dialog had to ask.
            */}
            <div style={{ display: 'flex', alignItems: 'center', minHeight: 34 }}>
                <span style={{ flexGrow: 1 }} />
                {progress}
            </div>
            {error && <div style={{ color: 'red', margin: 5 }}>{error}</div>}

            {!loaded && <div style={{ display: 'flex', alignItems: 'center', margin: 20 }}>
                <CircularProgress size={20} />
                <span style={{ marginLeft: 10, opacity: 0.7 }}>Loading algorithms…</span>
            </div>}

            {loaded && <TreeView
                expanded={expanded}
                onNodeToggle={this.onToggle.bind(this)}
                defaultCollapseIcon={<ExpandMoreIcon />}
                defaultExpandIcon={<ChevronRightIcon />}>

                <TreeItem nodeId="ns:users"
                    label={this.branchLabel('Private', userId ? <React.Fragment>
                        {this.action('New algorithm here', <AddIcon fontSize="small" />,
                            () => this.onNew(`users/${userId}`))}
                        {this.action('New plain folder here', <FolderIcon fontSize="small" />,
                            () => this.onNewFolder(`users/${userId}`))}
                    </React.Fragment> : null)}>
                    {priv.length ? priv.map((a) => this.algorithmNode(a)) : empty()}
                </TreeItem>

                <TreeItem nodeId="ns:shared"
                    label={this.branchLabel('Shared', this.action('New shared folder',
                        <CreateNewFolderIcon fontSize="small" />, () => this.onShare(null)))}>
                    {folders.length
                        ? folders.map((f) => <TreeItem key={f.name} nodeId={`folder:${f.name}`}
                            label={this.branchLabel(f.name, <React.Fragment>
                                {this.action('New algorithm in this folder', <AddIcon fontSize="small" />,
                                    () => this.onNew(`shared/${f.name}`), !this.mayWriteShared(f))}
                                {this.action('New plain folder in this folder', <FolderIcon fontSize="small" />,
                                    () => this.onNewFolder(`shared/${f.name}`), !this.mayWriteShared(f))}
                                {this.action('Change who this folder is shared with',
                                    <ShareIcon fontSize="small" />, () => this.onShare(f.name))}
                                {this.action(this.mayAdminShared(f)
                                    ? 'Delete this shared folder'
                                    : 'Only an owner with admin access can delete this folder',
                                <DeleteIcon fontSize="small" />,
                                () => this.onDeleteSharedFolder(f), !this.mayAdminShared(f))}
                            </React.Fragment>, `${(f.owners || []).length} member(s)`)}>
                            {sharedAlgorithms.filter((a) => sharedFolderOf(a) === f.name).length
                                ? sharedAlgorithms.filter((a) => sharedFolderOf(a) === f.name)
                                    .map((a) => this.algorithmNode(a))
                                : empty()}
                        </TreeItem>)
                        : empty()}
                </TreeItem>

                <TreeItem nodeId="ns:system"
                    label={this.branchLabel('System',
                        // Only an administrator can write here, so only they are offered the action.
                        isAdmin ? <React.Fragment>
                            {this.action('New built-in algorithm', <AddIcon fontSize="small" />,
                                () => this.onNew('system'))}
                            {this.action('New plain folder', <FolderIcon fontSize="small" />,
                                () => this.onNewFolder('system'))}
                        </React.Fragment> : null,
                        isAdmin ? '' : 'read only')}>
                    {system.length ? system.map((a) => this.algorithmNode(a)) : empty()}
                </TreeItem>
            </TreeView>}
            {/* The drawer first: a dialog is a portal and lands above it either way, but the editor should
                not be at the mercy of whatever set `dialog` last. */}
            {drawer}
            {dialog}
        </div>;
    }
}

/** Name a new algorithm and say where it goes. Shared by New and Copy — the question is the same one. */
/**
 * Name something, and say where it goes only when that is still an open question.
 *
 * `destination` fixes it — the node the action came from already decided — and the picker disappears. Copy is
 * the one case that still needs `destinations`, because the point of a copy is putting it somewhere else.
 */
class NameDialog extends React.Component {
    constructor(props) {
        super(props);
        const destinations = props.destinations || [];
        this.state = {
            id: props.defaultId || '',
            destination: props.destination || (destinations.length ? destinations[0].value : '')
        };
    }

    render() {
        const { title, label, destinations, helperText, onCancel, onDone } = this.props;
        const { id, destination } = this.state;
        // Three distinct cases, and only the middle one asks anything:
        //   New algorithm — `destination` is fixed by the node the action came from
        //   Copy          — `destinations` offered, and picking one IS the point
        //   Add file      — neither; the file goes into the algorithm and there is nothing to choose
        const choosing = !this.props.destination && Array.isArray(destinations) && destinations.length > 0;
        return <Dialog open={true}>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                <div>
                    <TextField autoFocus style={{ width: 320 }} label={label} value={id}
                        helperText={helperText || 'Letters, digits, dashes and underscores.'}
                        onChange={(e) => this.setState({ id: e.target.value })} />
                </div>
                {choosing && <FormControl style={{ width: 300, marginTop: 15 }}>
                    <InputLabel>Where</InputLabel>
                    <Select value={destination} onChange={(e) => this.setState({ destination: e.target.value })}>
                        {destinations.map((d) =>
                            <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>)}
                    </Select>
                </FormControl>}
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel}>Cancel</Button>
                {/* A destination is only required when there is one to pick. Requiring it unconditionally left
                    Add-file with an empty picker and a permanently disabled OK. */}
                <Button color="primary" disabled={!id || (choosing && !destination)}
                    onClick={() => onDone({ id, destination })}>OK</Button>
            </DialogActions>
        </Dialog>;
    }
}

/**
 * Create a shared folder, or change who it is shared with.
 *
 * The rule worth knowing while reading this: you cannot remove your own `admin` — the server puts it back. A
 * save that dropped the last admin could not be undone by anybody, so it is not allowed to happen, and the
 * dialog says so rather than letting it look like it worked.
 */
class ShareFolderDialog extends React.Component {
    constructor(props) {
        super(props);
        // Opened from a folder's Share action, so it starts on that folder rather than on "New folder…".
        const initial = props.initialName
            ? (props.folders || []).find((f) => f.name === props.initialName)
            : null;
        this.state = {
            name: initial ? initial.name : '',
            owners: initial ? (initial.owners || []) : this.seed(),
            creating: !initial,
            addUserId: ''
        };
    }

    /**
     * A new folder starts with its creator in it, as admin.
     *
     * The server does this too, but doing it only there meant the dialog looked as though it were about to
     * create an empty folder — and if the creator was a platform administrator it failed outright with
     * "needs at least one owner with admin access". Seeding it here means the rule is visible before Save.
     */
    seed() {
        return this.props.userId ? [{ userId: this.props.userId, access: 'admin' }] : [];
    }

    onPickFolder(name) {
        if (name === '') return this.setState({ creating: true, name: '', owners: this.seed() });
        const folder = (this.props.folders || []).find((f) => f.name === name);
        this.setState({ creating: false, name, owners: (folder && folder.owners) || [] });
    }

    /** The last admin cannot be demoted or removed — nobody could undo it afterwards. */
    lastAdmin(userId) {
        const admins = this.state.owners.filter((o) => o.access === 'admin');
        return admins.length === 1 && admins[0].userId === userId;
    }

    render() {
        const { appContext, folders, onCancel, onSave } = this.props;
        const { name, owners, creating, addUserId } = this.state;

        const ownerColumns = [
            // Same reason as the version history: the stored value is a UUID.
            {
                id: 'userId',
                label: 'User',
                formatValue: (v) => (v ? <User appContext={appContext} userId={v} /> : '')
            },
            {
                id: 'access',
                label: 'Access',
                // 🔒 Richard, 2026-08-23: "no ability to remove myself unless I set someone else as admin."
                // Enforced as a rule about the LAST admin rather than about the caller — the same reasoning
                // applies to demoting anyone who happens to be the only one left.
                formatValue: (v, row) => <Select value={v || 'read'}
                    disabled={this.lastAdmin(row.userId)}
                    onChange={(e) => this.setState({
                        owners: owners.map((o) => (o.userId === row.userId ? { ...o, access: e.target.value } : o))
                    })}>
                    {ACCESS_LEVELS.map((a) => <MenuItem key={a} value={a}>{a}</MenuItem>)}
                </Select>
            },
            {
                id: 'remove',
                label: '',
                formatValue: (v, row) => <Tooltip title={this.lastAdmin(row.userId)
                    ? 'The only admin cannot be removed — give someone else admin first'
                    : 'Remove from this folder'}>
                    <span>
                        <IconButton size="small" disabled={this.lastAdmin(row.userId)}
                            onClick={() => this.setState({
                                owners: owners.filter((o) => o.userId !== row.userId)
                            })}><DeleteIcon fontSize="small" /></IconButton>
                    </span>
                </Tooltip>
            }
        ];

        return <Dialog open={true} maxWidth="md" fullWidth>
            <DialogTitle>Shared folders</DialogTitle>
            <DialogContent>
                <FormControl style={{ width: 300 }}>
                    <InputLabel>Folder</InputLabel>
                    <Select value={creating ? '' : name} onChange={(e) => this.onPickFolder(e.target.value)}>
                        <MenuItem value="">New folder…</MenuItem>
                        {(folders || []).map((f) => <MenuItem key={f.name} value={f.name}>{f.name}</MenuItem>)}
                    </Select>
                </FormControl>
                {creating && <TextField style={{ width: 300, marginLeft: 15 }} label="Folder name" value={name}
                    helperText="Shared folder names are global — pick something specific."
                    onChange={(e) => this.setState({ name: e.target.value })} />}

                <div style={{ marginTop: 20, display: 'flex', alignItems: 'flex-end' }}>
                    <SelectUser appContext={appContext} userId={addUserId} label="Add a user"
                        onChange={(userId) => this.setState({ addUserId: userId })} />
                    <Button disabled={!addUserId || owners.some((o) => o.userId === addUserId)}
                        onClick={() => this.setState({
                            owners: [...owners, { userId: addUserId, access: 'read' }],
                            addUserId: ''
                        })}>Add</Button>
                </div>

                <div style={{ marginTop: 10 }}>
                    <EnhancedTable
                        disableActions={true}
                        disableSelect={true}
                        rowsPerPage={10}
                        orderBy="userId"
                        columnData={ownerColumns}
                        data={owners}
                        title="Shared with" />
                </div>
                <Typography variant="caption">
                    A folder always keeps at least one admin. Creating one makes you its admin.
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel}>Cancel</Button>
                <Tooltip title={!name
                    ? 'Name the folder first'
                    : (!owners.some((o) => o.access === 'admin')
                        ? 'Give at least one person admin access'
                        : '')}>
                    <span>
                        <Button color="primary"
                            disabled={!name || !owners.some((o) => o.access === 'admin')}
                            onClick={() => onSave({ name, owners })}>Save</Button>
                    </span>
                </Tooltip>
            </DialogActions>
        </Dialog>;
    }
}

export default MLAlgorithmsView;
